import { type FrameName } from "@/game/entities/config";
import { narratives } from "@/game/entities/config/quests/narrative/narratives";
import {
	QuestMetaActions,
	questMetaActionsConfig,
} from "@/game/entities/config/quests/quest_meta_actions";
import { QuestObjectiveActions } from "@/game/entities/config/quests/quest_objective_actions";
import { QuestInProgressStates } from "@/game/entities/config/quests/quest_states.ts";
import { QuestTypes } from "@/game/entities/config/quests/quest_types";
import { quests } from "@/game/entities/config/quests/quests";
import { ResourceType } from "@/game/entities/config/resources";
import { settlementNames } from "@/game/entities/config/settlements";
import { isTerrain, TileVisibilityType } from "@/game/entities/config/terrain";
import useEmitter, { EmitterEvents, TileLabelAction } from "@/hooks/useEmitter";
import useTileRepository, {
	isTilerepositoryInitialized,
} from "@/hooks/useTileRepository";
import router from "@/router";
import type { QuestItem } from "@/types/quest.ts";
import {
	COINS_QUEST_REWARD_CONST,
	type NarrativeItem,
	type QuestActive,
	type QuestTimestampEntry,
} from "@/types/quest.ts";
import { TileEntityParam, TileLabelSkin } from "@/types/tile";
import { RouteNames } from "@/types/ui";
import { getAmount } from "@/utils/number";
import {
	createImmutableState,
	getImmutableState,
	getImmutableStateProperty,
	setImmutableState,
	setImmutableStateProperty,
} from "@/utils/state";
import { defineStore } from "pinia";
import { computed, type ComputedRef, type Ref } from "vue";
import useResourcesStorageStore from "./useResourcesStorageStore";
import useResourcesStore from "./useResourcesStore";
import useSettlementsStore from "./useSettlementsStore";

export type QuestsStoreState = {
	// Quest state
	activeQuests: Array<QuestActive>;
	finishedQuests: Array<QuestActive | QuestTimestampEntry | number>;
	rewardsByTick: Partial<Record<ResourceType, number>>;
	// Narrative state
	activeNarrativeId: number | null;
	activeNarrativeChapter: number | null;
	// Tutorial state
	tutorialActive: boolean;
	isTutorialHighlightingUI: boolean;
	tutorialStep: number | null;
	trackedQuestId: number | null;
};

type QuestsStorePayload = {
	state: QuestsStoreState;
};

type QuestStore = {
	$getPayload: () => QuestsStorePayload | null;
	$resetPayload: () => void;
	$setState: (value: Partial<QuestsStoreState>) => void;
	activeQuests: Ref<Array<QuestActive>>;
	finishedQuests: ComputedRef<
		Array<QuestActive | QuestTimestampEntry | number>
	>;
	addQuest(questId: number, progress?: number): void;
	updateQuestProgress(questId: number, amount: number): number;
	setQuestProgress(questId: number, progress: number): number;
	getQuestById(questId: number): QuestActive | null;
	getQuestProgress(questId: number): number;
	getActiveQuests: ComputedRef<Array<QuestActive>>;
	getFinishedQuests(): Array<QuestActive>;
	getFinishedQuestsIds(): Array<number>;
	resetQuests(): void;
	finishQuest(questId: number): void;
	setPayedQuestState(questId: number): void;
	updateBuiltBuildingCount(questId: number): void;
	updateResourceIncomeProgress(questId: number): void;
	rewardsByTick: ComputedRef<Partial<Record<ResourceType, number>>>;
	addRewardByTick(resourceType: ResourceType, amount: number): void;
	updateTerrainChangeCount(questId: number, frameName: FrameName): void;
	// Narrative API
	activeNarrativeId: ComputedRef<number | null>;
	activeNarrativeChapter: ComputedRef<number | null>;
	startNarrative(quest: QuestActive): void;
	stopNarrative(): void;
	nextNarrativeChapter(chapterId: number): void;
	currentNarrative: ComputedRef<NarrativeItem | null>;
	// Tutorial API
	tutorialActive: ComputedRef<boolean>;
	isTutorialHighlightingUI: ComputedRef<boolean>;
	setIsTutorialHighlightingUI(value: boolean): void;
	tutorialStep: ComputedRef<number>;
	setTutorialStep(step: number | null): void;
	stopTutorial(): void;
	// Intro
	isIntroActive: ComputedRef<boolean>;
	checkActiveQuestDefinitions(): void;
	getLastCompletedMainQuest(): QuestActive | null;
	analyzeQuestState(): void;
	resetDailyQuests(): void;
	trackedQuestId: Ref<number | null>;
	setTrackedQuest(questId: number | null): void;
};

const STORE_NAME = "quests";

export default defineStore(STORE_NAME, (): QuestStore => {
	const state = createImmutableState<QuestsStoreState>({
		activeQuests: [],
		finishedQuests: [],
		rewardsByTick: {} as Partial<Record<ResourceType, number>>,
		activeNarrativeId: null,
		activeNarrativeChapter: null,
		tutorialActive: true,
		isTutorialHighlightingUI: false,
		tutorialStep: 0,
		trackedQuestId: null,
	});

	// Used only for save.
	// DO NOT USE IN GAME!
	const $getState = (): QuestsStoreState => {
		const data = getImmutableState(state).value;
		const keys = Object.keys(data) as Array<keyof QuestsStoreState>;
		return keys.reduce(
			(
				acc: QuestsStoreState,
				key: keyof QuestsStoreState,
			): QuestsStoreState => {
				let serializedValue:
					| Array<QuestActive>
					| Array<number | QuestTimestampEntry>
					| undefined = undefined;
				if (key === "activeQuests") {
					serializedValue = (data[key] as Array<QuestActive>).map(
						(quest: QuestActive): QuestActive => {
							if (!("type" in quest.objective)) return quest;
							return {
								...quest,
								objective: {
									...quest.objective,
									// @ts-expect-error - ocakava Set, ale my sme z neho spravili Array
									type:
										typeof quest.objective.type !== "string"
											? Array.from(quest.objective.type)
											: quest.objective.type,
									amount: getAmount(quest.objective.amount),
								},
								reward: {
									...quest.reward,
									// @ts-expect-error - typy, nemozne vyriesit
									content: quest.reward.content.map(
										rewardItem => ({
											...rewardItem,
											amount: getAmount(
												rewardItem.amount,
											),
										}),
									),
								},
							};
						},
					);
				}
				if (key === "finishedQuests") {
					serializedValue = (
						data[key] as Array<
							QuestActive | QuestTimestampEntry | number
						>
					).map((quest): number | QuestTimestampEntry => {
						if (typeof quest === "number") return quest;
						const completedAt =
							"completed_at" in quest
								? quest.completed_at
								: undefined;
						if (quest.started_at || completedAt) {
							return {
								id: quest.id,
								...(quest.started_at && {
									started_at: quest.started_at,
								}),
								...(completedAt && {
									completed_at: completedAt,
								}),
							};
						}
						return quest.id;
					});
				}

				return {
					...acc,
					[key]: serializedValue || data[key],
				};
			},
			{} as QuestsStoreState,
		);
	};

	// Used only for save.
	// DO NOT USE IN GAME!
	let $isPatched: boolean = false;

	// Used only for save.
	// DO NOT USE IN GAME!
	let $payloadInProgress: QuestsStorePayload | null = null;

	// Used only for save.
	// DO NOT USE IN GAME!
	const $getPayload = (): QuestsStorePayload | null => {
		if (!$isPatched) return null;
		if ($payloadInProgress !== null) return $payloadInProgress;
		return ($payloadInProgress = {
			state: $getState(),
		});
	};

	// Used only for save.
	// DO NOT USE IN GAME!
	const $resetPayload = (): void => {
		$payloadInProgress = null;
	};

	// Used only for save.
	// DO NOT USE IN GAME!
	const $setState = (value: Partial<QuestsStoreState>): void => {
		(Object.keys(value) as Array<keyof QuestsStoreState>).forEach(
			(key: keyof QuestsStoreState): void => {
				if (key === "activeQuests") {
					value[key as "activeQuests" | "finishedQuests"] = (
						value[key] as Array<QuestActive>
					).map((quest: QuestActive): QuestActive => {
						if (!("type" in quest.objective)) return quest;
						return {
							...quest,
							objective: {
								...quest.objective,
								// @ts-expect-error - ocakava Set, ale my sme z neho spravili Array
								type:
									typeof quest.objective.type !== "string"
										? new Set(quest.objective.type)
										: quest.objective.type,
							},
						};
					});
				}
				// Fix starej struktury, kde boli resources kapitalkami.
				if (key === "rewardsByTick") {
					if (
						value[key] &&
						COINS_QUEST_REWARD_CONST in value[key] &&
						typeof value[key][COINS_QUEST_REWARD_CONST] === "number"
					) {
						value[key][ResourceType.COINS] =
							value[key][COINS_QUEST_REWARD_CONST];
						delete value[key][COINS_QUEST_REWARD_CONST];
					}
				}
			},
		);
		setImmutableState(state, value);
		setTimeout((): void => {
			$isPatched = true;
		}, 0);
	};

	const activeQuests = computed((): Array<QuestActive> => {
		if (!isTilerepositoryInitialized()) return state.value.activeQuests;
		return state.value.activeQuests.map(
			(quest: QuestActive): QuestActive => {
				const questConfig = quests.get(quest.id);

				if (
					questConfig?.objective.action ===
					QuestObjectiveActions.USE_RESOURCES
				) {
					const resourceType = questConfig?.objective.type;
					const resource = useResourcesStore()[resourceType] ?? 0;
					if (resource && quest.payed === 0) {
						return { ...quest, progress: resource };
					} else if (resource && quest.payed === 1) {
						return {
							...quest,
							progress: getAmount(quest.objective.amount),
						};
					} else {
						return { ...quest, progress: 0 };
					}
				} else if (
					questConfig?.objective.action ===
					QuestObjectiveActions.HAVE_RESOURCE
				) {
					const resourceType = questConfig?.objective.type;
					const resource = useResourcesStore()[resourceType] ?? 0;
					return { ...quest, progress: resource };
				} else if (
					questConfig?.objective.action ===
					QuestObjectiveActions.HAVE_STORAGE
				) {
					const resourceType = questConfig?.objective.type;
					const resource =
						useResourcesStorageStore()[resourceType] ?? 0;
					return { ...quest, progress: resource };
				} else if (
					questConfig?.objective.action ===
					QuestObjectiveActions.BUILD_BUILDING
				) {
					// For BUILD_BUILDING quests loaded from save, preserve the saved state as-is
					// Only recalculate if this is a newly added quest (no built_building baseline)
					if (
						quest.built_building !== undefined &&
						quest.built_building !== null
					) {
						// Quest was loaded from save - preserve the saved progress and baseline
						return quest;
					}

					// This is a newly added quest - calculate initial state from tile repository
					const useTileRepo = useTileRepository();
					// If objective.type is a Set of FrameNames, we need to sum counts of all
					let currentBuildingCount = 0;
					if (questConfig.objective.type instanceof Set) {
						for (const frameName of questConfig.objective.type) {
							currentBuildingCount +=
								useTileRepo.getBuildingCountByFrameName(
									frameName,
								);
						}
					} else {
						currentBuildingCount =
							useTileRepo.getBuildingCountByFrameName(
								questConfig.objective.type,
							);
					}

					// Set baseline to current count and progress to 0 for new quests
					return {
						...quest,
						progress: 0,
						built_building: currentBuildingCount,
					};
				}
				return quest;
			},
		);
	});

	const finishedQuests = getImmutableStateProperty(
		state,
		"finishedQuests",
		[],
	);

	const resetQuests = (): void => {
		setImmutableStateProperty(state, "activeQuests", []);
		setImmutableStateProperty(state, "finishedQuests", []);
	};

	const resetDailyQuests = (): void => {
		//remove daily quests from finished quests
		const nonDailyQuestsFinished = finishedQuests.value.filter(
			(quest: QuestActive | QuestTimestampEntry | number): boolean => {
				if (typeof quest === "number" || !("type" in quest)) {
					const questId =
						typeof quest === "number" ? quest : quest.id;
					const questConfig = quests.get(questId);
					return questConfig?.type !== QuestTypes.DAILY;
				}
				return quest.type !== QuestTypes.DAILY;
			},
		);
		setImmutableStateProperty(
			state,
			"finishedQuests",
			nonDailyQuestsFinished,
		);
		// remove daily quests from active quests and add new ones
		const nonDailyQuestsActive = activeQuests.value.filter(
			(quest: QuestActive): boolean => quest.type !== QuestTypes.DAILY,
		);
		setImmutableStateProperty(state, "activeQuests", [
			...nonDailyQuestsActive,
			...getNewDailyQuests(),
		]);
	};

	function getNewDailyQuests(): Array<QuestActive> {
		const dailyQuests = Array.from(quests.entries())
			.filter(
				([, quest]: [number, QuestItem]): boolean =>
					quest.type === QuestTypes.DAILY,
			)
			.map(
				([id, quest]: [number, QuestItem]): QuestActive => ({
					...quest,
					id,
					progress: 0,
					objective: {
						...quest.objective,
						amount: getAmount(quest.objective.amount),
					},
					// set reward to reward, but amount of every reward to getAmount
					reward: {
						...quest.reward,
						// @ts-expect-error - typy, nemozne vyriesit
						content: quest.reward.content.map(rewardItem => ({
							...rewardItem,
							amount: getAmount(rewardItem.amount),
						})),
					},
				}),
			);
		return dailyQuests;
	}

	function addQuest(questId: number, progress: number = 0): void {
		if (!quests.has(questId)) return;

		const quest = quests.get(questId)!;
		const existingQuest = activeQuests.value.find(
			(q: QuestActive): boolean => q.id === questId,
		);
		if (existingQuest) return;

		if (quest.badgeCoordinates) {
			const skin =
				quest.type === QuestTypes.MAIN
					? TileLabelSkin.QUEST_MAIN_Q
					: TileLabelSkin.QUEST_SIDE_Q;
			useEmitter().emit(EmitterEvents.PhaserUpdateTileLabel, {
				coordinates: quest.badgeCoordinates!,
				skin,
				action: TileLabelAction.CREATE,
			});
		}

		let calculatedProgress = progress;

		if (quest.type === QuestTypes.MAIN) {
			setTrackedQuest(questId);
		}
		switch (quest.objective.action) {
			case QuestObjectiveActions.HAVE_TERRAIN: {
				const useTileRepo = useTileRepository();

				let isTrrn = false;
				[...quest.objective.type].forEach((frameName: string): void => {
					if (isTerrain(frameName as FrameName)) {
						isTrrn = true;
					}
				});
				if (isTrrn) {
					calculatedProgress = useTileRepo.getTileCount0(
						quest.objective.type as Set<FrameName>,
						getAmount(quest.objective.amount),
					);
				} else {
					calculatedProgress = useTileRepo.getTileCount2(
						quest.objective.type as Set<FrameName>,
						getAmount(quest.objective.amount),
					);
				}
				break;
			}
			case QuestObjectiveActions.HAVE_FULL_SETTLEMENTS: {
				// Go through all settlementEntities and call their haveFullSettlements getter count it and save it as the progress
				const tileRepo = useTileRepository();

				let settlementAmount = tileRepo.getFullSettlementsCount() ?? 0;

				if (getAmount(quest.objective.amount) < settlementAmount) {
					settlementAmount = getAmount(quest.objective.amount);
				}

				calculatedProgress = settlementAmount;
				break;
			}
			case QuestObjectiveActions.HAVE_SPECIFIC_SETTLEMENT_ON_LEVEL: {
				const actualSettlement = settlementNames.get(
					quest.objective.settlement_id,
				);
				if (!actualSettlement) return;
				const settlementStore = useSettlementsStore();
				const settlementLevel = settlementStore.getLevelByCoordinates({
					x: actualSettlement.x,
					y: actualSettlement.y,
				});
				if (settlementLevel) {
					calculatedProgress = settlementLevel.value;
				}
				break;
			}
			case QuestObjectiveActions.HAVE_SETTLEMENTS_ON_LEVEL: {
				const tileRepo = useTileRepository();
				const settlementAmount =
					tileRepo.getActiveSettlementCountOnLevelOrHigher(
						quest.objective.level,
					) ?? 0;
				calculatedProgress = settlementAmount;
				break;
			}
			case QuestObjectiveActions.HAVE_SETTLEMENTS_AXIS: {
				const tileRepo = useTileRepository();
				const settlementAmount =
					tileRepo.getActiveSettlementCountOnLevelOrHigherAndAxis(
						quest.objective.level,
						quest.objective.axis,
					);
				calculatedProgress = settlementAmount;
				break;
			}
			case QuestObjectiveActions.USE_RESOURCES: {
				const resourceType = quest.objective.type;
				const resource = useResourcesStore()[resourceType] ?? 0;
				calculatedProgress = resource;

				// We'll set the questData with payed property later
				// Create questData here to include payed property
				const questData: QuestActive = {
					id: questId,
					progress: calculatedProgress,
					payed: 0,
					...quest,
				};

				setImmutableStateProperty(state, "activeQuests", [
					...activeQuests.value,
					questData,
				]);

				startNarrative(questData);
				return;
			}
			case QuestObjectiveActions.DISCOVER_SPECIFIC_TILE: {
				const useTileRepo = useTileRepository();
				const tile = useTileRepo.getTileEntityByTileCoordinates({
					x: quest.objective.coordination.x,
					y: quest.objective.coordination.y,
				});

				if (
					tile &&
					tile.getParamByName(TileEntityParam.VISIBILITY) !==
						TileVisibilityType.EXPLORED
				) {
					useEmitter().emit(EmitterEvents.PingSpecificTile, {
						x: quest.objective.coordination.x,
						y: quest.objective.coordination.y,
					});
				} else {
					calculatedProgress = 1;
				}
				break;
			}
			case QuestObjectiveActions.BUILD_BUILDING: {
				const useTileRepo = useTileRepository();

				// if type is Set of FrameNames, we need to sum counts of all frameNames
				let buildingCount = 0;
				if (quest.objective.type instanceof Set) {
					for (const frameName of quest.objective.type) {
						buildingCount +=
							useTileRepo.getBuildingCountByFrameName(frameName);
					}
				} else {
					buildingCount = useTileRepo.getBuildingCountByFrameName(
						quest.objective.type,
					);
				}
				calculatedProgress = buildingCount;
				break;
			}
			default:
				break;
		}

		const questData: QuestActive = {
			id: questId,
			progress: calculatedProgress,
			...quest,
		};

		// Add built_building tracking for BUILD_BUILDING quests
		if (quest.objective.action === QuestObjectiveActions.BUILD_BUILDING) {
			questData.built_building = calculatedProgress; // Store baseline count when quest starts
			// Only set progress to 0 for new quests, preserve progress for loaded quests
			if (progress === 0) {
				questData.progress = 0; // Progress should start at 0 for new build quests
			}
		}
		if (
			quest.objective.action ===
			QuestObjectiveActions.INCREASE_RESOURCE_INCOME
		) {
			const resourceType = quest.objective.type;
			const resourceIncome = useResourcesStore()[resourceType];
			questData.initial_state = resourceIncome; // Store initial resource amount for INCREASE_RESOURCE_INCOME quests
			questData.progress = 0; // Progress should start at 0 for INCREASE_RESOURCE_INCOME quests
		}

		setImmutableStateProperty(state, "activeQuests", [
			...activeQuests.value,
			questData,
		]);

		startNarrative(questData);

		const questMetaConfig = questMetaActionsConfig[questId];
		if (
			questMetaConfig &&
			questMetaConfig.action === QuestMetaActions.START_CHAPTER_NARRATIVE
		) {
			useEmitter().emit(EmitterEvents.PhaserLoadAudio);
		}
	}

	// Check if quests definition is updated if the activequest is loaded with old definition from server
	function checkActiveQuestDefinitions(): void {
		const activeQuestsArray = activeQuests.value;
		const updatedActiveQuests = activeQuestsArray.map(
			(quest: QuestActive): QuestActive => {
				if (quest.type === QuestTypes.DAILY) {
					return quest; // Daily quests can be different, we don't want to override them
				}
				if (quests.has(quest.id)) {
					return {
						...quest,
						...quests.get(quest.id),
					};
				}
				return quest;
			},
		);
		setImmutableStateProperty(state, "activeQuests", updatedActiveQuests);
	}

	// --- Narrative Section (merged) ---
	const activeNarrativeId = getImmutableStateProperty(
		state,
		"activeNarrativeId",
		null,
	);

	const activeNarrativeChapter = getImmutableStateProperty(
		state,
		"activeNarrativeChapter",
		null,
	);

	function startNarrative(quest: QuestActive): void {
		if ([QuestTypes.MAIN, QuestTypes.SIDE].includes(quest.type)) {
			const narrativeId = quest.id;
			const narrativeArray: NarrativeItem[] = Array.from(
				narratives.values(),
			).flat();
			const getActualNarrative = narrativeArray.filter(
				(narrative: NarrativeItem): boolean =>
					narrative.quest_id === quest.id &&
					narrative.order_number === 1,
			);
			if (getActualNarrative.length === 0) return;
			setImmutableStateProperty(state, "activeNarrativeId", narrativeId);
			setImmutableStateProperty(state, "activeNarrativeChapter", 1);
		}
	}

	function stopNarrative(): void {
		setImmutableStateProperty(state, "activeNarrativeId", null);
		setImmutableStateProperty(state, "activeNarrativeChapter", null);
	}

	const currentNarrative = computed<NarrativeItem | null>(() => {
		const narrativeArray: NarrativeItem[] = Array.from(
			narratives.values(),
		).flat();
		const getActualNarrative = narrativeArray.filter(
			(narrative: NarrativeItem): boolean =>
				narrative.quest_id === activeNarrativeId.value &&
				narrative.order_number === activeNarrativeChapter.value,
		);
		return getActualNarrative[0] || null;
	});

	function nextNarrativeChapter(chapterId: number): void {
		setImmutableStateProperty(state, "activeNarrativeChapter", chapterId);
		const actualNarrative = currentNarrative.value;
		const hasActiveNarrative =
			activeNarrativeId.value !== null &&
			activeNarrativeChapter.value !== null &&
			actualNarrative !== null;
		if (!hasActiveNarrative) stopNarrative();
	}

	function setQuestProgress(questId: number, progress: number): number {
		const quest = getQuestById(questId);
		if (!quest || !quests.has(questId))
			return QuestInProgressStates.NON_EXISTING;

		const maxProgressValue = getAmount(
			quests.get(questId)!.objective.amount,
		);
		const newProgressValue = Math.min(progress, maxProgressValue);

		if (newProgressValue >= maxProgressValue) {
			setImmutableStateProperty(
				state,
				"activeQuests",
				activeQuests.value.map(
					(q: QuestActive): QuestActive =>
						q.id === questId
							? { ...q, progress: newProgressValue }
							: q,
				),
			);
			return QuestInProgressStates.FINISHED;
		} else {
			setImmutableStateProperty(
				state,
				"activeQuests",
				activeQuests.value.map(
					(q: QuestActive): QuestActive =>
						q.id === questId
							? { ...q, progress: newProgressValue }
							: q,
				),
			);

			return QuestInProgressStates.IN_PROGRESS;
		}
	}

	function updateQuestProgress(questId: number, amount: number): number {
		const quest = getQuestById(questId);
		if (!quest || !quests.has(questId))
			return QuestInProgressStates.NON_EXISTING;

		const newProgressValue = quest.progress + amount;
		const maxProgressValue = getAmount(
			quests.get(questId)!.objective.amount,
		);

		if (newProgressValue >= maxProgressValue) {
			setImmutableStateProperty(
				state,
				"activeQuests",
				activeQuests.value.map(
					(q: QuestActive): QuestActive =>
						q.id === questId
							? { ...q, progress: newProgressValue }
							: q,
				),
			);
			return QuestInProgressStates.FINISHED;
		} else {
			setImmutableStateProperty(
				state,
				"activeQuests",
				activeQuests.value.map(
					(q: QuestActive): QuestActive =>
						q.id === questId
							? { ...q, progress: newProgressValue }
							: q,
				),
			);

			return QuestInProgressStates.IN_PROGRESS;
		}
	}

	function setPayedQuestState(questId: number): void {
		const quest = getQuestById(questId);
		if (!quest || !quests.has(questId)) return;

		setImmutableStateProperty(
			state,
			"activeQuests",
			activeQuests.value.map(
				(q: QuestActive): QuestActive =>
					q.id === questId ? { ...q, ...{ payed: 1 } } : q,
			),
		);
	}

	function updateTerrainChangeCount(
		questId: number,
		frameName: FrameName,
	): void {
		// Get the quest from the computed activeQuests (which preserves loaded state)
		const quest = activeQuests.value.find(q => q.id === questId);
		if (
			!quest ||
			quest.objective.action !== QuestObjectiveActions.CHANGE_TERRAIN
		)
			return;

		// if the objective type is the frameName, increment progress by 1
		if (quest.objective.type === frameName) {
			const newProgress = quest.progress + 1;
			// Update the raw state directly to ensure changes persist
			setImmutableStateProperty(
				state,
				"activeQuests",
				state.value.activeQuests.map(
					(q: QuestActive): QuestActive =>
						q.id === questId
							? {
									...q,
									progress: newProgress,
								}
							: q,
				),
			);
		}
	}

	function updateBuiltBuildingCount(questId: number): void {
		// Get the quest from the computed activeQuests (which preserves loaded state)
		const quest = activeQuests.value.find(q => q.id === questId);
		if (
			!quest ||
			quest.objective.action !== QuestObjectiveActions.BUILD_BUILDING
		)
			return;

		// Get current building count of the required type
		const useTileRepo = useTileRepository();
		// If quest.objective.type is a Set, we should foreach and sum the counts
		let currentBuildingCount = 0;
		if (quest.objective.type instanceof Set) {
			Array.from(quest.objective.type).forEach(
				(frameName: FrameName): void => {
					currentBuildingCount +=
						useTileRepo.getBuildingCountByFrameName(frameName);
				},
			);
		} else {
			currentBuildingCount = useTileRepo.getBuildingCountByFrameName(
				quest.objective.type,
			);
		}

		// Calculate the expected current building count based on quest state
		const baselineCount = quest.built_building || 0;
		const expectedCurrentCount = baselineCount + quest.progress;

		// Calculate the difference - this handles both building and demolition
		const countDifference = currentBuildingCount - expectedCurrentCount;

		// Only update if there's actually a difference to avoid unnecessary updates
		if (countDifference === 0) {
			return;
		}

		// Adjust progress by the difference, ensuring it doesn't go below 0 or above max
		const maxProgress = getAmount(quest?.objective.amount || 0);
		const newProgress = Math.max(
			0,
			Math.min(quest.progress + countDifference, maxProgress),
		);

		// Update the raw state directly to ensure changes persist
		setImmutableStateProperty(
			state,
			"activeQuests",
			state.value.activeQuests.map(
				(q: QuestActive): QuestActive =>
					q.id === questId
						? {
								...q,
								progress: newProgress,
							}
						: q,
			),
		);
	}

	function updateResourceIncomeProgress(questId: number): void {
		// Get the quest from the computed activeQuests (which preserves loaded state)
		const quest = activeQuests.value.find(
			(q: QuestActive): boolean => q.id === questId,
		);
		if (
			!quest ||
			quest.objective.action !==
				QuestObjectiveActions.INCREASE_RESOURCE_INCOME
		)
			return;

		const resourceType = quest.objective.type;
		const currentIncome = useResourcesStore()[resourceType];
		const baselineIncome = quest.initial_state || 0;
		const incomeDifference = currentIncome - baselineIncome;

		const maxProgress = getAmount(quest?.objective.amount || 0);
		const newProgress = Math.max(
			0,
			Math.min(incomeDifference, maxProgress),
		);
		setImmutableStateProperty(
			state,
			"activeQuests",
			state.value.activeQuests.map(
				(q: QuestActive): QuestActive =>
					q.id === questId
						? {
								...q,
								progress: newProgress,
							}
						: q,
			),
		);
	}

	function finishQuest(questId: number): void {
		const quest = getQuestById(questId);
		if (!quest || !quests.has(questId)) return;

		const newProgressValue = quest.progress;
		const maxProgressValue = getAmount(
			quests.get(questId)!.objective.amount,
		);

		if (newProgressValue >= maxProgressValue) {
			setImmutableStateProperty(
				state,
				"activeQuests",
				activeQuests.value.filter(
					(q: QuestActive): boolean => q.id !== questId,
				),
			);
			setImmutableStateProperty(state, "finishedQuests", [
				...finishedQuests.value,
				{
					...quest,
					id: questId,
					progress: maxProgressValue,
				},
			]);
			if (quest.badgeCoordinates) {
				const skin =
					quest.type === QuestTypes.MAIN
						? TileLabelSkin.QUEST_MAIN_Q
						: TileLabelSkin.QUEST_SIDE_Q;
				useEmitter().emit(EmitterEvents.PhaserUpdateTileLabel, {
					coordinates: quest.badgeCoordinates!,
					skin,
					action: TileLabelAction.DELETE,
				});
			}
		}
	}

	function getQuestById(questId: number): QuestActive | null {
		if (!activeQuests.value) return null;
		return (
			Object.values(activeQuests.value)
				.flat()
				.find((q: QuestActive): boolean => q.id === questId) || null
		);
	}

	function getLastCompletedMainQuest(): QuestActive | null {
		if (!getFinishedQuests()) return null;
		const sortedFinishedQuests = [...getFinishedQuests()]
			.filter(
				(quest: QuestActive): boolean => quest.type === QuestTypes.MAIN,
			)
			.sort((a: QuestActive, b: QuestActive): number => b.id - a.id);
		return sortedFinishedQuests.length > 0 ? sortedFinishedQuests[0] : null;
	}

	function analyzeQuestState(): void {
		if (
			activeQuests.value.some(
				(quest: QuestActive): boolean => quest.type === QuestTypes.MAIN,
			)
		) {
			return;
		}
		// need to check if the final quest finished is the last quest in questConfig. If not add the next quest and route to questView.
		const lastQuestDone = getLastCompletedMainQuest();
		if (!lastQuestDone) return;
		const nextQuest = quests.get(lastQuestDone.id + 1);
		if (nextQuest) {
			router.push({ name: RouteNames.QUESTS });
			addQuest(lastQuestDone.id + 1);
		}
	}

	function getQuestProgress(questId: number): number {
		return getQuestById(questId)?.progress ?? 0;
	}

	const getActiveQuests = computed(
		(): Array<QuestActive> => Object.values(activeQuests.value).flat(),
	);

	function getFinishedQuests(): Array<QuestActive> {
		const finished = finishedQuests.value || [];
		return finished
			.map(
				(
					quest: QuestActive | QuestTimestampEntry | number,
				): QuestActive | null => {
					if (typeof quest === "number") {
						const questConfig = quests.get(quest);
						if (!questConfig) return null;
						return {
							...questConfig,
							id: quest,
							progress: getAmount(questConfig.objective.amount),
						} as QuestActive;
					}
					if (!("type" in quest)) {
						const questConfig = quests.get(quest.id);
						if (!questConfig) return null;
						return {
							...questConfig,
							id: quest.id,
							progress: getAmount(questConfig.objective.amount),
						} as QuestActive;
					}
					return quest;
				},
			)
			.filter(
				(quest: QuestActive | null): quest is QuestActive =>
					quest !== null,
			);
	}

	function getFinishedQuestsIds(): Array<number> {
		return finishedQuests.value.map(
			(quest: QuestActive | QuestTimestampEntry | number): number =>
				typeof quest === "number" ? quest : quest.id,
		);
	}

	const rewardsByTick = getImmutableStateProperty(
		state,
		"rewardsByTick",
		{} as Partial<Record<ResourceType, number>>,
	);

	const addRewardByTick = (
		resourceType: ResourceType,
		amount: number,
	): void => {
		const currentAmount = state.value.rewardsByTick[resourceType] || 0;
		setImmutableStateProperty(state, "rewardsByTick", {
			...state.value.rewardsByTick,
			[resourceType]: currentAmount + amount,
		});
	};

	// --- Tutorial Section (merged) ---
	const tutorialActive = getImmutableStateProperty(
		state,
		"tutorialActive",
		false,
	);

	const isTutorialHighlightingUI = getImmutableStateProperty(
		state,
		"isTutorialHighlightingUI",
		false,
	);

	const setIsTutorialHighlightingUI = (value: boolean): void => {
		setImmutableStateProperty(state, "isTutorialHighlightingUI", value);
	};

	const tutorialStep = getImmutableStateProperty(state, "tutorialStep", 0);

	const setTutorialStep = (step: number): void => {
		setImmutableStateProperty(state, "tutorialStep", step);
	};

	const stopTutorial = (): void => {
		setImmutableStateProperty(state, "tutorialActive", false);
		setImmutableStateProperty(state, "tutorialStep", 1);
		useEmitter().emit(EmitterEvents.TutorialEnd);
	};

	// ---Preload Section (merged) ---
	const isIntroActive = computed((): boolean => {
		return (
			tutorialActive.value &&
			tutorialStep.value <= 1 &&
			activeQuests.value.length === 0
		);
	});

	const trackedQuestId = getImmutableStateProperty(
		state,
		"trackedQuestId",
		null,
	);

	const setTrackedQuest = (questId: number | null): void => {
		setImmutableStateProperty(state, "trackedQuestId", questId);
	};

	return {
		$getPayload,
		$resetPayload,
		$setState,
		activeQuests,
		finishedQuests,
		addQuest,
		setQuestProgress,
		updateQuestProgress,
		getQuestById,
		getQuestProgress,
		getActiveQuests,
		getFinishedQuests,
		getFinishedQuestsIds,
		resetQuests,
		finishQuest,
		setPayedQuestState,
		updateBuiltBuildingCount,
		updateResourceIncomeProgress,
		updateTerrainChangeCount,
		rewardsByTick,
		addRewardByTick,
		checkActiveQuestDefinitions,
		analyzeQuestState,
		// Narrative API
		activeNarrativeId,
		activeNarrativeChapter,
		startNarrative,
		stopNarrative,
		nextNarrativeChapter,
		currentNarrative,
		getLastCompletedMainQuest,
		// Tutorial API
		tutorialActive,
		isTutorialHighlightingUI,
		setIsTutorialHighlightingUI,
		tutorialStep,
		setTutorialStep,
		stopTutorial,
		// Intro API
		isIntroActive,
		resetDailyQuests,
		trackedQuestId,
		setTrackedQuest,
	};
});
